/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from '@/components/typography';
import type { AtpStatus } from '@/lib/atp/atp-fields';
import { resolveAtpDeliveryMessage } from '@/lib/atp/atp-delivery-message';
import { formatDeliveryDate } from '@/lib/date-utils';
import { cn } from '@/lib/utils';

interface CartDeliveryEstimateProps {
    /** The basket product item carrying the `c_atp*` fields. */
    productItem: unknown;
    /**
     * Optional basket-level ATP status gate. The cartridge only writes the
     * per-item `c_atp*` fields when the basket is enriched, so this is rarely
     * needed in production — anything other than `'enriched'` hard-hides the
     * message.
     */
    basketStatus?: AtpStatus | null;
    className?: string;
    /**
     * When true, suppresses the `'unavailable'` variant. Used by ProductItem
     * to hide the plain-text unavailable message from the right column so it
     * can instead be rendered below the Remove button with a TriangleAlert icon.
     */
    hideUnavailable?: boolean;
}

/**
 * Renders the ATP delivery estimate ("Get It By {date}" / "Ships by {date}" /
 * backorder / unavailable) for a single cart or mini-cart line item.
 *
 * Pure renderer: it reads the `c_atp*` fields the `int_sfnext_atp` cartridge
 * already enriched onto the basket response — no client-side fetching, no
 * basket/router coupling. Returns `null` when there is nothing to show
 * (partner-fulfilled item, no usable date, or an explicit non-enriched
 * `basketStatus`), so the slot collapses cleanly.
 */
export default function CartDeliveryEstimate({
    productItem,
    basketStatus,
    className,
    hideUnavailable = false,
}: CartDeliveryEstimateProps): ReactElement | null {
    const { t, i18n } = useTranslation('delivery');

    const message = resolveAtpDeliveryMessage(productItem, basketStatus);
    if (!message) {
        return null;
    }
    if (hideUnavailable && message.variant === 'unavailable') {
        return null;
    }

    const locale = i18n.language;
    let text: string | null = null;
    let tone: 'positive' | 'muted' | 'negative' = 'muted';

    switch (message.variant) {
        case 'getItBy': {
            const date = formatDeliveryDate(message.date ?? undefined, locale) ?? message.date;
            if (!date) return null;
            text = t('getItBy', { date });
            tone = 'positive';
            break;
        }
        case 'shipsBy': {
            const date = formatDeliveryDate(message.date ?? undefined, locale) ?? message.date;
            if (!date) return null;
            text = t('shipsBy', { date });
            tone = 'muted';
            break;
        }
        case 'backordered': {
            const start = formatDeliveryDate(message.rangeStart ?? undefined, locale) ?? message.rangeStart;
            const end = formatDeliveryDate(message.rangeEnd ?? undefined, locale) ?? message.rangeEnd;
            const range = start && end ? `${start} \u2013 ${end}` : start || end;
            if (!range) return null;
            text = t('backordered', { range });
            tone = 'muted';
            break;
        }
        case 'unavailable': {
            text = t('unavailable');
            tone = 'negative';
            break;
        }
        default:
            return null;
    }

    return (
        <Typography
            variant="small"
            data-testid="cart-delivery-estimate"
            data-atp-variant={message.variant}
            className={cn(
                'text-xs',
                tone === 'positive' && 'text-foreground font-medium',
                tone === 'muted' && 'text-muted-foreground',
                tone === 'negative' && 'text-destructive',
                className
            )}>
            {text}
        </Typography>
    );
}
